import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ImageUploader from '../components/ImageUploader';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const getAuthHeaders = () => {
  const token = localStorage.getItem('adminToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const ArticlesManagement = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [notification, setNotification] = useState(null);
  const [filter, setFilter] = useState('all'); // all | pending | approved | rejected
  const [formData, setFormData] = useState({
    title_ru: '',
    title_en: '',
    content_ru: '',
    content_en: '',
    preview_ru: '',
    preview_en: '',
    image_url: '',
    video_url: '',
    category: 'article',
    is_active: true
  });

  useEffect(() => {
    loadArticles();
  }, []);

  const loadArticles = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/articles`, { headers: getAuthHeaders() });
      setArticles(response.data);
    } catch (error) {
      console.error('Error loading articles:', error);
      showNotification('Ошибка загрузки статей', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const handleOpenModal = (article = null) => {
    if (article) {
      setEditingArticle(article);
      setFormData({
        title_ru: article.title_ru,
        title_en: article.title_en,
        content_ru: article.content_ru,
        content_en: article.content_en,
        preview_ru: article.preview_ru,
        preview_en: article.preview_en,
        image_url: article.image_url || '',
        video_url: article.video_url || '',
        category: article.category || 'article',
        is_active: article.is_active
      });
    } else {
      setEditingArticle(null);
      setFormData({
        title_ru: '',
        title_en: '',
        content_ru: '',
        content_en: '',
        preview_ru: '',
        preview_en: '',
        image_url: '',
        video_url: '',
        category: 'article',
        is_active: true
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingArticle(null);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingArticle) {
        await axios.put(`${API_URL}/api/admin/articles/${editingArticle.id}`, formData, { headers: getAuthHeaders() });
        showNotification('Статья успешно обновлена');
      } else {
        await axios.post(`${API_URL}/api/admin/articles`, formData, { headers: getAuthHeaders() });
        showNotification('Статья успешно создана');
      }
      handleCloseModal();
      loadArticles();
    } catch (error) {
      console.error('Error saving article:', error);
      showNotification('Ошибка сохранения статьи', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Удалить эту статью?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/articles/${id}`, { headers: getAuthHeaders() });
      showNotification('Статья успешно удалена');
      loadArticles();
    } catch (error) {
      console.error('Error deleting article:', error);
      showNotification('Ошибка удаления статьи', 'error');
    }
  };

  const moderateArticle = async (id, status) => {
    try {
      await axios.put(`${API_URL}/api/admin/articles/${id}/moderation`, { status }, { headers: getAuthHeaders() });
      const labels = { approved: 'Статья опубликована', rejected: 'Статья отклонена', pending: 'Возвращена на модерацию' };
      showNotification(labels[status]);
      loadArticles();
    } catch (error) {
      console.error('Error moderating article:', error);
      showNotification('Ошибка модерации', 'error');
    }
  };

  const statusOf = (a) => a.moderation_status || 'approved';
  const sourceLabel = (a) => {
    if (a.source === 'rss_parser') return 'RSS';
    if (a.source === 'ai_agent') return 'AI-агент';
    return 'Вручную';
  };
  const counts = {
    all: articles.length,
    pending: articles.filter((a) => statusOf(a) === 'pending').length,
    approved: articles.filter((a) => statusOf(a) === 'approved').length,
    rejected: articles.filter((a) => statusOf(a) === 'rejected').length,
  };
  const visibleArticles = filter === 'all' ? articles : articles.filter((a) => statusOf(a) === filter);

  const statusBadge = (status) => {
    const map = {
      approved: ['bg-green-100 text-green-800', 'Опубликовано'],
      pending: ['bg-yellow-100 text-yellow-800', 'На модерации'],
      rejected: ['bg-gray-200 text-gray-600', 'Отклонено'],
    };
    const [cls, label] = map[status] || map.approved;
    return <span className={`px-2 py-1 text-xs rounded-full ${cls}`}>{label}</span>;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('ru-RU');
  };

  if (loading) {
    return <div className="text-center py-12">Загрузка...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Управление статьями</h1>
          <p className="text-gray-600 mt-2">Новости и статьи</p>
        </div>
        <button
          onClick={() => handleOpenModal()}
          className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-2 rounded-lg font-medium transition"
        >
          + Добавить статью
        </button>
      </div>

      {notification && (
        <div className={`p-4 rounded-lg ${
          notification.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'
        }`}>
          {notification.message}
        </div>
      )}

      {/* Moderation filter tabs */}
      <div className="flex flex-wrap gap-2 border-b border-gray-200" data-testid="articles-filter-tabs">
        {[
          ['all', 'Все'],
          ['pending', 'На модерации'],
          ['approved', 'Опубликовано'],
          ['rejected', 'Отклонено'],
        ].map(([key, label]) => (
          <button
            key={key}
            onClick={() => setFilter(key)}
            data-testid={`articles-filter-${key}`}
            className={`px-4 py-2 font-medium transition border-b-2 -mb-px ${filter === key ? 'border-orange-500 text-orange-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          >
            {label}
            <span className={`ml-2 text-xs px-1.5 py-0.5 rounded-full ${key === 'pending' && counts.pending > 0 ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-500'}`}>{counts[key]}</span>
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-6">
        {visibleArticles.length === 0 && (
          <div className="text-center py-10 text-gray-500 bg-white rounded-lg">Нет статей в этой категории.</div>
        )}
        {visibleArticles.map((article) => (
          <div key={article.id} className="bg-white rounded-lg shadow-sm p-6 flex items-start space-x-4" data-testid={`article-card-${article.id}`}>
            {article.image_url && (
              <img src={article.image_url} alt={article.title_ru} className="w-32 h-32 object-cover rounded-lg flex-shrink-0" />
            )}
            <div className="flex-grow">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{article.title_ru}</h3>
                  <p className="text-gray-600 text-sm mb-2">{article.preview_ru}</p>
                  <div className="flex items-center flex-wrap gap-3 text-sm text-gray-500">
                    <span>Дата: {formatDate(article.published_date)}</span>
                    {statusBadge(statusOf(article))}
                    <span className="px-2 py-1 text-xs rounded-full bg-blue-50 text-blue-700">{sourceLabel(article)}</span>
                    <span className="px-2 py-1 text-xs rounded-full bg-purple-50 text-purple-700">{article.category === 'news' ? 'Новость' : 'Статья'}</span>
                  </div>
                </div>
                <div className="flex flex-col items-end space-y-2">
                  <div className="flex items-center space-x-2">
                    {statusOf(article) !== 'approved' && (
                      <button onClick={() => moderateArticle(article.id, 'approved')} data-testid={`article-approve-${article.id}`} className="text-green-600 hover:text-green-800 font-medium">
                        Одобрить
                      </button>
                    )}
                    {statusOf(article) !== 'rejected' && (
                      <button onClick={() => moderateArticle(article.id, 'rejected')} data-testid={`article-reject-${article.id}`} className="text-gray-500 hover:text-gray-700 font-medium">
                        {statusOf(article) === 'approved' ? 'Снять с публикации' : 'Отклонить'}
                      </button>
                    )}
                  </div>
                  <div className="flex items-center space-x-2">
                    <button onClick={() => handleOpenModal(article)} className="text-blue-600 hover:text-blue-800 font-medium">
                      Редактировать
                    </button>
                    <button onClick={() => handleDelete(article.id)} className="text-red-600 hover:text-red-800 font-medium">
                      Удалить
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <h2 className="text-2xl font-bold text-gray-900">
                {editingArticle ? 'Редактировать статью' : 'Создать статью'}
              </h2>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Заголовок (RU) *</label>
                  <input type="text" name="title_ru" value={formData.title_ru} onChange={handleInputChange} required className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Заголовок (EN) *</label>
                  <input type="text" name="title_en" value={formData.title_en} onChange={handleInputChange} required className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Превью (RU) *</label>
                  <textarea name="preview_ru" value={formData.preview_ru} onChange={handleInputChange} rows={3} required className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Превью (EN) *</label>
                  <textarea name="preview_en" value={formData.preview_en} onChange={handleInputChange} rows={3} required className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Контент (RU) * <span className="text-xs text-gray-500">HTML</span></label>
                  <textarea name="content_ru" value={formData.content_ru} onChange={handleInputChange} rows={10} required className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 font-mono text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Контент (EN) * <span className="text-xs text-gray-500">HTML</span></label>
                  <textarea name="content_en" value={formData.content_en} onChange={handleInputChange} rows={10} required className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 font-mono text-sm" />
                </div>
              </div>

              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Медиафайлы (необязательно)</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <ImageUploader
                      value={formData.image_url}
                      onChange={(url) => setFormData({...formData, image_url: url})}
                      label="Изображение статьи"
                      maxSize={10}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">URL видео</label>
                    <input type="url" name="video_url" value={formData.video_url} onChange={handleInputChange} placeholder="https://youtube.com/watch?v=..." className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500" />
                    <p className="text-xs text-gray-500 mt-1">Ссылка на YouTube, Vimeo и т.д.</p>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Категория</label>
                <select 
                  name="category" 
                  value={formData.category} 
                  onChange={handleInputChange} 
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                >
                  <option value="article">Статья</option>
                  <option value="news">Новость</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">Статьи создаются вручную, новости импортируются автоматически</p>
              </div>

              <div className="flex items-center pt-4">
                <input type="checkbox" name="is_active" checked={formData.is_active} onChange={handleInputChange} className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded" />
                <label className="ml-2 block text-sm text-gray-900">Активна (отображается на сайте)</label>
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button type="button" onClick={handleCloseModal} className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
                  Отмена
                </button>
                <button type="submit" className="px-6 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg">
                  {editingArticle ? 'Сохранить' : 'Создать'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ArticlesManagement;